import { useEffect, useState } from 'react'
import { Dialog, IconButton, Popover, Stack, Typography } from '@mui/material'
import ConfirmModalUI from '@/components/common/ConfirmModalUI/ConfirmModalUI'
import useTranslation from '@/hooks/useTranslation'
import CloseIcon from '@mui/icons-material/Close'
import FlowRulePanel from './FlowRulePanel'
import { RuleSection } from './RuleRail'
import { useRuleCopy } from './use-rule-copy'

interface FlowSectionPopoverProps {
  open: boolean
  /**
   * Where the Configure chip was when clicked — the popup opens from it.
   * `left` is the chip's RIGHT edge: the panel hangs leftwards from there (see
   * `transformOrigin`), because these chips sit far enough right that a panel
   * opening rightwards ran off the screen.
   */
  anchorPosition: { top: number; left: number }
  section: Exclude<RuleSection, 'alarm'>
  interfaceId: number
  pipeId: number
  pipeName?: string
  flowRuleId: number
  /** Metric l/m, so a new valve can default to the pipe it feeds. */
  pipeCapacity?: number
  onClose: () => void
  onRefetchPipes?: () => Promise<unknown> | void
}

const PANEL_BG = '#0e2b27'
const PANEL_INK = '#eef7f4'
const PANEL_MUTED = '#8fb0a9'

// Widths only: the labels are translated, so they are resolved inside the
// component where t() is available.
const SECTION_WIDTH: Record<FlowSectionPopoverProps['section'], number> = {
  // Wide enough for the grid inside to lay out without clipping its last
  // columns. Measured, not guessed: the valves grid needs 1327px of columns in
  // bulk edit (Delays splits into Start and Stop, and the row action appears),
  // plus the card's 36px of padding and borders. maxWidth below keeps them on
  // screen when the window is narrower, at the cost of scrolling inside.
  learning: 520,
  // Sensors need far less room now: the cards are compact and the edit table
  // is down to Status, Decoder and the row action.
  sensors: 600,
  // Reading width. The valves table needs 1370 and asks for it when bulk edit
  // opens (see VALVES_EDIT_WIDTH); the cards that replace it do not, and a
  // panel sized for the table left most of itself empty.
  valves: 640,
}

const VALVES_EDIT_WIDTH = 1210

/**
 * The learning / sensors / valves workspaces as popovers off their Configure
 * chips — the same pattern as the flow alarm, so all four buttons on a pipe
 * row behave identically. Widths differ per section: the learning card is a
 * small form, the valve grid needs room for its columns.
 */
const FlowSectionPopover = ({
  open,
  anchorPosition,
  section,
  interfaceId,
  pipeId,
  pipeName,
  flowRuleId,
  pipeCapacity,
  onClose,
  onRefetchPipes,
}: FlowSectionPopoverProps) => {
  const copy = useRuleCopy()
  const { t } = useTranslation()
  const sectionLabel = {
    learning: copy.learning,
    sensors: copy.sensors,
    valves: copy.valves,
  }[section]
  const [valvesEditing, setValvesEditing] = useState(false)
  // SCRUM-1032 QC item 6 — staged valve edits that closing would discard.
  const [valvesDirty, setValvesDirty] = useState(false)
  const [sensorsDirty, setSensorsDirty] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  // A different pipe or section starts from the reading width again.
  useEffect(() => setValvesEditing(false), [section, pipeId])

  /**
   * SCRUM-1032 QC item 6 — the Close button is the only way out.
   *
   * This panel holds a grid the user types into, and an MUI Popover closes on a
   * backdrop click or Escape by default: one stray click outside and a row of
   * valve settings is gone with no warning. Dismissal is refused outright, and
   * the X asks first when there is staged work behind it.
   */
  const requestClose = () => {
    if (valvesDirty || sensorsDirty) {
      setConfirmDiscard(true)
      return
    }
    onClose()
  }

  const discardAndClose = () => {
    setConfirmDiscard(false)
    setValvesDirty(false)
    setSensorsDirty(false)
    onClose()
  }

  const width =
    section === 'valves' && valvesEditing ? VALVES_EDIT_WIDTH : SECTION_WIDTH[section]

  return (
    <>
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      // Deliberately inert: see requestClose. A backdrop click or Escape must
      // not discard what is staged in the grid.
      onClose={undefined}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{
        paper: {
          sx: {
            mt: '6px',
            borderRadius: '12px',
            width: `${width}px`,
            transition: 'width 180ms ease',
            maxWidth: 'calc(100vw - 32px)',
            overflow: 'hidden',
            boxShadow:
              '0 6px 16px rgba(16, 24, 40, 0.16), 0 20px 44px rgba(16, 24, 40, 0.18)',
          },
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'flex-start',
          gap: '8px',
          backgroundColor: PANEL_BG,
          p: '12px 18px 11px',
        }}
      >
        <Stack sx={{ gap: '1px', flex: 1, minWidth: 0 }}>
          {/* The section is what the panel IS, so it reads at full strength
              rather than as a muted eyebrow; the pipe name below says which
              pipe it belongs to. */}
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: PANEL_INK,
            }}
          >
            {sectionLabel}
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 680,
              color: PANEL_INK,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {pipeName || `Pipe ${pipeId}`}
          </Typography>
        </Stack>
        <IconButton
          size="small"
          onClick={requestClose}
          sx={{ color: PANEL_MUTED, mt: '-2px', mr: '-6px' }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>

      <FlowRulePanel
        interfaceId={interfaceId}
        pipeId={pipeId}
        flowRuleId={flowRuleId}
        pipeCapacity={pipeCapacity}
        section={section}
        onRefetchPipes={onRefetchPipes}
        onClose={onClose}
        onValvesEditModeChange={setValvesEditing}
        onValvesDirtyChange={setValvesDirty}
        onSensorsDirtyChange={setSensorsDirty}
      />

    </Popover>

    {/* Asked before anything staged in the grid is thrown away.
        A sibling of the Popover, not a child: a Dialog nested inside an open
        Popover is a modal inside a modal, and MUI's focus and aria-hidden
        management leaves the inner one unreachable. */}
    <Dialog
      open={confirmDiscard}
      onClose={() => setConfirmDiscard(false)}
      // Above the Popover it is asking about, whatever order the two portals
      // happen to land in.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
    >
      <ConfirmModalUI
        title={t('formsUnsavedChangesTitle')}
        text={t('formsUnsavedChangesBody')}
        cancelText={t('formsActionsKeepEditing')}
        confirmText={t('formsActionsDiscardChanges')}
        onClose={() => setConfirmDiscard(false)}
        onConfirm={discardAndClose}
      />
    </Dialog>
    </>
  )
}

export default FlowSectionPopover
